import { createContext, type JSX, useContext, useState } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import {
	cancelLogin as cancelLoginService,
	performLogin,
	performSSOLogin,
	refreshToken as refreshTokenService,
} from "../../services/login";
import type { BankIdMode } from "../../services/types";
import {
	getBearerToken,
	getRefreshToken,
	removeTokens,
	setBankIdMode,
	setBearerToken,
	setRefreshToken,
} from "../../utils/storage";
import { jwtParser } from "./jwt-parser";
import type { Tokens, UserState } from "./types";
import { getUserManager, type OidcUserState, type UserSettings } from "./user-manager";

type LoginContextType = {
	doLogin: (isSameDevice: boolean, mode?: BankIdMode, ssn?: string | null) => Promise<void>;
	doSSOLogin: (
		settings: UserSettings,
		urlParams: URLSearchParams,
		state?: OidcUserState,
		acr_values?: string,
		max_age?: string,
		scope?: string,
	) => ReturnType<typeof performSSOLogin>;
	doLogout: (silent?: boolean) => Promise<void>;
	cancelLogin: () => Promise<void>;
	refreshTokens: () => Promise<void>;
	tokens: Tokens;
	qrCode: string | null;
	startTime: number;
	loginState: UserState;
	isSameDevice: boolean;
	baseUrl: string;
	appVersion: string;
	extendTimeout: () => void;
	getTimeRemaining: () => () => number;
	maxTime: number;
};

const LoginContext = createContext<LoginContextType>({
	doLogin: async () => new Promise(() => {}),
	doSSOLogin: () => new Promise(() => {}),
	doLogout: () => Promise.resolve(),
	cancelLogin: () => Promise.resolve(),
	refreshTokens: () => Promise.resolve(),
	tokens: {
		bearerToken: "",
		refreshToken: "",
	},
	qrCode: null,
	startTime: 0,
	loginState: "PENDING",
	isSameDevice: false,
	extendTimeout: () => {},
	getTimeRemaining: () => () => 0,
	maxTime: 0,
	baseUrl: "",
	appVersion: "",
});

export const LoginProvider = ({
	baseUrl,
	appVersion,
	afterLogout,
	maxTime = 300 * 1000,
	initialValues,
	children,
}: {
	children: JSX.Element | JSX.Element[];
	afterLogout?: () => void;
	baseUrl: string;
	appVersion: string;
	maxTime?: number;
	initialValues?: Partial<
		Pick<
			LoginContextType,
			"tokens" | "qrCode" | "loginState" | "isSameDevice" | "extendTimeout" | "getTimeRemaining"
		>
	>;
}) => {
	const [isSameDevice, setIsSameDevice] = useState(initialValues?.isSameDevice ?? false);
	const [bankIdToken, setBankIdToken] = useState<string>();
	const [tokens, setTokens] = useState<Tokens>({
		bearerToken: initialValues?.tokens?.bearerToken || getBearerToken(),
		refreshToken: initialValues?.tokens?.refreshToken || getRefreshToken(),
	});
	const [qrCode, setQrCode] = useState<string | null>(initialValues?.qrCode ?? "");
	const [extendTimeout, setExtendTimeout] = useState<(() => void) | null>(
		initialValues?.extendTimeout ?? null,
	);
	const [initialized, setInitialized] = useState(false);
	const [getTimeRemaining, setGetTimeRemaining] = useState<(() => number) | null>(
		initialValues?.getTimeRemaining ?? null,
	);
	const [loginState, setLoginState] = useState<UserState>(initialValues?.loginState ?? "PENDING");

	const initialize = () => {
		setInitialized(true);

		if (!tokens.bearerToken) {
			setLoginState("LOGGED_OUT");
			return;
		}

		const parsedToken = jwtParser(tokens.bearerToken);
		if (parsedToken.scope.includes("sveabank.gateway.listTenants")) {
			setLoginState("LIST_TENANTS");
		} else {
			setLoginState("LOGGED_IN");
		}
	};

	if (!initialized) {
		initialize();
	}

	const doLogout = async (silent = false) => {
		try {
			const userManager = getUserManager();
			userManager.signoutSilent().catch((_) => {}); // Handle iframe timeout error
		} catch {
			// Not signed in over SSO, skipping.
		}

		removeTokens();
		setTokens({
			bearerToken: getBearerToken(),
			refreshToken: getRefreshToken(),
		});
		setLoginState("LOGGED_OUT");
		if (!silent) afterLogout?.();
	};

	const cancelLogin = () => {
		if (!bankIdToken) return;
		setLoginState("CANCELLING");
		return cancelLoginService(baseUrl, appVersion, bankIdToken);
	};

	const doLogin = async (
		isSameDevice: boolean,
		pMode: BankIdMode = "PROD",
		ssn?: string | null,
	) => {
		setIsSameDevice(isSameDevice);
		setBankIdMode(pMode);
		setLoginState("PENDING");

		try {
			const {
				extendTimeout,
				getTimeRemaining: getTimeRemainingFunc,
				tokens: tokenPromise,
			} = performLogin(
				baseUrl,
				appVersion,
				pMode,
				setQrCode,
				setBankIdToken,
				maxTime,
				isSameDevice,
				ssn,
			);

			setExtendTimeout(() => extendTimeout);
			setGetTimeRemaining(() => getTimeRemainingFunc);
			const tokens = await tokenPromise;

			setBearerToken(tokens.bearerToken);
			setRefreshToken(tokens.refreshToken);
			setLoginState("LOGGED_IN");
			setTokens(tokens);
		} catch (error) {
			setLoginState("LOGGED_OUT");
			throw error;
		}
	};

	/**
	 * When logging in as tenant we set tenant to undefined because we have not yet selected tenant.
	 * Doing a SSO login allows us to get a token good enough to list our available tenants. This sets us in LIST_TENANTS state.
	 * When we have selected on we do a normal login using doLogin() and the selected tenant.
	 */
	const doSSOLogin = async (
		settings: UserSettings,
		urlParams: URLSearchParams,
		state?: OidcUserState,
		acr_values?: string,
		max_age?: string,
		scope?: string,
	) => {
		setLoginState("PENDING");

		const userManager = getUserManager(settings);

		const userInfo = await performSSOLogin(
			userManager,
			urlParams,
			state,
			acr_values,
			max_age,
			scope,
		);

		if (!userInfo?.bearerToken) throw new Error("Missing bearerToken");

		setBearerToken(userInfo.bearerToken);
		setRefreshToken(userInfo?.refreshToken || "");

		const parsedToken = jwtParser(userInfo.bearerToken);

		if (parsedToken.scope.includes("sveabank.gateway.listTenants")) {
			setLoginState("LIST_TENANTS");
		} else {
			setLoginState("LOGGED_IN");
		}

		setTokens({
			bearerToken: userInfo.bearerToken,
			refreshToken: userInfo?.refreshToken || "",
		});

		return userInfo;
	};

	/** IMPORTANT! After using this userInfo needs to be invalidated and refreshed */
	const refreshTokens = async () => {
		if (!tokens.refreshToken) throw new Error("No refresh token found");
		const response = await refreshTokenService(baseUrl, appVersion, tokens.refreshToken);
		setBearerToken(response.bearerToken);
		setRefreshToken(response.refreshToken);

		setTokens({
			bearerToken: response.bearerToken,
			refreshToken: response.refreshToken,
		});
	};

	// TODO: use JSX/TSX instead. This is being used because PostCSS complains that it cant find this file if it's called .tsx.
	return _jsx(LoginContext.Provider, {
		value: {
			doLogin,
			doSSOLogin,
			doLogout,
			cancelLogin,
			refreshTokens,
			tokens,
			qrCode,
			loginState,
			baseUrl,
			appVersion,
			isSameDevice,
			extendTimeout,
			getTimeRemaining,
			maxTime,
		},
		children: children,
	});
};

export const useLogin = () => useContext(LoginContext);
